'use strict';

import { Hmap } from '../../threejs/Hmap';
import { TerrainEnvironment } from '../../components/Terrain/Terrain';
import * as dat from 'dat.gui';
import { Color } from 'three';

const vertexHead = `varying vec3 vWorldPos;`;
const vertexBody = `vWorldPos = (modelMatrix * vec4( transformed,1.0)).xyz;`;

const head = `
        uniform float lutSize;
        uniform float nightLightsDensity;
        uniform float nightLightsScale;
        uniform vec3 nightLightsColor;
        uniform bool useNightLights;
        varying vec3 vWorldPos;
        uniform float nightLutSize;
		#if USE_3DTEXTURE
		precision highp sampler3D;
		uniform sampler3D lut3d;
		uniform sampler3D nightLut3d;
		#else
		uniform sampler2D lut;
		uniform sampler2D nightLut;
		
		vec3 lutLookup( sampler2D tex, float size, vec3 rgb ) {

			float sliceHeight = 1.0 / size;
			float yPixelHeight = 1.0 / ( size * size );

			// Get the slices on either side of the sample
			float slice = rgb.b * size;
			float interp = fract( slice );
			float slice0 = slice - interp;
			float centeredInterp = interp - 0.5;

			float slice1 = slice0 + sign( centeredInterp );

			// Pull y sample in by half a pixel in each direction to avoid color
			// bleeding from adjacent slices.
			float greenOffset = clamp( rgb.g * sliceHeight, yPixelHeight * 0.5, sliceHeight - yPixelHeight * 0.5 );

			vec2 uv0 = vec2(
				rgb.r,
				slice0 * sliceHeight + greenOffset
			);
			vec2 uv1 = vec2(
				rgb.r,
				slice1 * sliceHeight + greenOffset
			);

			vec3 sample0 = texture2D( tex, uv0 ).rgb;
			vec3 sample1 = texture2D( tex, uv1 ).rgb;

			return mix( sample0, sample1, abs( centeredInterp ) );

		}
		
		#endif
		uniform float lutIntensity;
		uniform float nightLutIntensity;
		uniform float timeAlpha;
		
		vec3 calculateUVW(vec4 color,float size){
		    float pixelWidth = 1.0 / size;
            float halfPixelWidth = 0.5 / size;
            vec3 uvw = vec3( halfPixelWidth ) + color.rgb * ( 1.0 - pixelWidth );
            
            return uvw;
		}
		
		
		vec4 getLutLookup(vec4 color,sampler3D lut,float size) {
            vec3 uvw = calculateUVW(color,size);
            vec4 lutVal = vec4( texture( lut, uvw ).rgb, color.a );

            return lutVal;
        }
       
        float rand(float n){return fract(sin(n) * 43758.5453123);}
        
        float noise(vec2 n) {
            const vec2 d = vec2(0.0, 1.0);
            vec2 b = floor(n), f = smoothstep(vec2(0.0), vec2(1.0), fract(n));
            return mix(mix(rand(b), rand(b + d.yx), f.x), mix(rand(b + d.xy), rand(b + d.yy), f.x), f.y);
        }
`;
const body = `
        vec4 val = gl_FragColor;
        vec4 dayLookup;
        vec4 nightLookup;

        #if USE_3DTEXTURE
        
        dayLookup = getLutLookup(val,lut3d,lutSize);
        nightLookup = getLutLookup(val,nightLut3d,nightLutSize);

        #else
        vec3 dayUVw = calculateUVW(val,lutSize);
        vec3 nightUVw = calculateUVW(val,nightLutSize);
        dayLookup = vec4( lutLookup( lut, lutSize, dayUVw ), val.a );
        nightLookup = vec4( lutLookup( nightLut, nightLutSize, nightUVw ), val.a );

        #endif
        
        vec4 dayColor = mix(val,dayLookup,lutIntensity);
        vec4 nightColor = mix(val,nightLookup,nightLutIntensity);
        
        if (useNightLights) {
            vec2 scaled_uv  = vWorldPos.xz;
            scaled_uv  *= nightLightsScale;
            float noise_value = noise(scaled_uv);
            vec4 lightsColor = vec4(nightLightsColor, 1.0);
            noise_value *= timeAlpha;
            nightColor = mix(nightColor,noise_value * lightsColor + nightColor,noise_value * nightLightsDensity);
        }
        
        gl_FragColor = mix(dayColor,nightColor,timeAlpha);
	`;

export class LUT {
    static scopes = {};
    #shaders = {};

    constructor (type) {
        this.type = type;
        this.lutsPresets = {
            dayLut: {
                name: null,
                intensity: 0,
                lut: null
            },
            nightLut: {
                name: null,
                intensity: 0,
                lut: null
            }
        };
        this.timeAlpha = 0;
        this.nightLightsSettings = {
          active: false,
          scale: 0,
          density: 0,
          color: new Color()
        };
        
        LUT.scopes[type] = this;
    }
    
    updateTime(alpha) {
        if (this.lutsPresets.nightLut.lut) {
            this.timeAlpha = 1 - alpha;
            
            for (let id in this.#shaders) {
                this.#shaders[id].uniforms.timeAlpha.value = this.timeAlpha;
            }
        }
    }

    set lutConfig(data) {
        if (data.lutsPresets) {
            Object.keys(data.lutsPresets).forEach(dayTime => {
                Object.entries(data.lutsPresets[dayTime]).forEach(([lutKey,lutValue]) => {
                    this.lutsPresets[dayTime][lutKey] = lutValue;
                })
            });
        } else {
            // Old lut type
            this.lutsPresets.dayLut.name = data.name;
            this.lutsPresets.dayLut.intensity = data.intensity;
            this.lutsPresets.dayLut.lut = data.lut;
        }
    }
    
    applyNightLightsSettings(settings) {
        this.nightLightsSettings = settings;
    }

    include(material) {
        //if (!this.lutsPresets.dayLut.lut) return;
        
        if (material.isShaderMaterial) {
            material.uniforms.lut3d = {
                value: this.lutsPresets.dayLut.lut.texture3D
            };

            material.uniforms.lutSize = {
                value: this.lutsPresets.dayLut.lut.texture3D.image.width
            };

            material.uniforms.lutIntensity = {
                value: this.lutsPresets.dayLut.intensity
            };

        } else if (Array.isArray(material)){
            material.map(subMaterial => {
                if (subMaterial.customPrograms) {
                    subMaterial.customPrograms.push(this.upgradeShader.bind(this));
                } else {
                    subMaterial.customPrograms = [this.upgradeShader.bind(this)];
                }
            });
        } else {
            if (material.customPrograms) {
                material.customPrograms.push(this.upgradeShader.bind(this));
            } else {
                material.customPrograms = [this.upgradeShader.bind(this)];
            }
        }
    }

    upgradeShader(shader,material) {
        if (shader.defines) {
            shader.defines.USE_3DTEXTURE = 1;
        } else {
            shader.defines = {
                USE_3DTEXTURE: 1
            };
        }
    
        shader.uniforms.lutIntensity = {
            value: this.lutsPresets.dayLut.intensity
        };
        
        shader.uniforms.useNightLights = {
            value: this.nightLightsSettings.active
        };
        
        shader.uniforms.nightLightsScale = {
            value: this.nightLightsSettings.scale
        };
    
        shader.uniforms.nightLightsDensity = {
            value: this.nightLightsSettings.density
        };
        
        shader.uniforms.nightLightsColor = {
            value: this.nightLightsSettings.color
        };
    
        shader.uniforms.nightLutIntensity = {
            value: this.lutsPresets.nightLut.intensity
        };
    
        shader.uniforms.nightLut = {
            value: null
        };
    
        shader.uniforms.lut = {
            value: null
        };
    
        if (this.lutsPresets.dayLut.lut) {
            shader.uniforms.lut3d = {
                value: this.lutsPresets.dayLut.lut.texture3D
            };
        
            shader.uniforms.lutSize = {
                value: this.lutsPresets.dayLut.lut.texture3D.image.width
            };
        } else {
            shader.uniforms.lut3d = {
                value: null
            };
        
            shader.uniforms.lutSize = {
                value: 0.0
            };
        }
    
        if (this.lutsPresets.nightLut.lut) {
            shader.uniforms.nightLut3d = {
                value: this.lutsPresets.nightLut.lut.texture3D
            };
            shader.uniforms.nightLutSize = {
                value: this.lutsPresets.nightLut.lut.texture3D.image.width
            }
        } else {
            shader.uniforms.nightLut3d = {
                value: null
            };
            shader.uniforms.nightLutSize = {
                value: 0.0
            }
        }
    
        shader.uniforms.timeAlpha = {
            value: this.timeAlpha
        };
        
        shader.vertexShader = shader.vertexShader.replace(
            '#include <clipping_planes_pars_vertex>',
            [
                '#include <clipping_planes_pars_vertex>',
                vertexHead
            ].join('\n')
        );
        
        shader.vertexShader = shader.vertexShader.replace(
            '#include <project_vertex>',
            [
                '#include <project_vertex>',
                vertexBody
            ].join('\n')
        );
    
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <clipping_planes_pars_fragment>',
            [
                '#include <clipping_planes_pars_fragment>',
                head
            ].join( '\n' )
        );
    
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <opaque_fragment>',
            [
                '#include <opaque_fragment>',
                body
            ].join( '\n' )
        );

        if (this.type === 'tiles') {
            Hmap.upgradeShader(shader);
        } else if (this.type === 'terrain') {
            TerrainEnvironment.upgradeShader(shader);
        }
        
        this.#shaders[material.uuid] = shader;
    }
    
    removeShader(uuid) {
        delete this.#shaders[uuid];
    }

    static getScope(type) {
        return LUT.scopes[type];
    }

}
